import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { api } from "@/redux/api";
import { buildPortfolioPath } from "@/utils/route-context";
import ContractList from "@/containers/contract/ContractList";
import { useAuthEditable } from "@/hooks/use-auth";

export default function PortfolioContractPage() {
  const { companyId: routeCompanyId } = useParams();
  const selectedCompanyId = useSelector((state) => state.Company?.selectedId);
  const userCompanies = useSelector((state) => state.Auth?.company);
  const userFacilities = useSelector((state) => state.Auth?.user_facilities);
  const companyId = routeCompanyId || selectedCompanyId;
  const [selectedFacilityIds, setSelectedFacilityIds] = useState([]);
  // Contractor directory powering the "Awarded Contractor" filter, aggregated
  // across every facility the portfolio user can see.
  const [awardedContractorIds, setAwardedContractorIds] = useState([]);
  const [contractorOptions, setContractorOptions] = useState([]);
  const canCreate = useAuthEditable("contract");

  const facilities = useMemo(() => {
    const accessibleFacilities = Array.isArray(userFacilities) ? userFacilities : [];
    return accessibleFacilities
      .filter((facility) => String(facility.company_id) === String(companyId))
      .map((facility) => ({
        id: facility.id,
        name: facility.name,
      }));
  }, [companyId, userFacilities]);

  const companies = useMemo(() => {
    const matchedCompany = (Array.isArray(userCompanies) ? userCompanies : []).find(
      (company) => String(company?.id ?? company?.Id ?? company?.company_id) === String(companyId)
    );
    return companyId ? [{
      id: companyId,
      name: matchedCompany?.name || matchedCompany?.label || `Company #${companyId}`,
    }] : [];
  }, [companyId, userCompanies]);

  useEffect(() => {
    setSelectedFacilityIds(facilities.map((facility) => facility.id));
  }, [companyId, facilities]);

  const facilityIdKey = facilities.map((facility) => facility.id).join(",");

  useEffect(() => {
    const facilityIds = facilityIdKey ? facilityIdKey.split(",").map(Number) : [];
    if (!facilityIds.length) {
      setContractorOptions([]);
      return undefined;
    }
    let active = true;
    api.getContractorDirectory(facilityIds)
      .then((response) => {
        if (!active) return;
        setContractorOptions(
          (response.data || []).map((company) => ({
            id: company.id,
            name: company.name,
            email: company.email,
            logo: company.logo,
          }))
        );
      })
      .catch(() => {
        if (active) setContractorOptions([]);
      });
    return () => {
      active = false;
    };
  }, [facilityIdKey]);

  // Reset the filter when the portfolio switches companies, otherwise the
  // selection would keep hiding every row behind an id from the old scope.
  useEffect(() => {
    setAwardedContractorIds([]);
  }, [companyId]);

  const loadRequests = useCallback(
    (facilityIds, params) => api.getContracts(facilityIds, params),
    []
  );
  const getDetailPath = useCallback(
    (record) => {
      return buildPortfolioPath(`contract-request-detail/${record.id}`, { companyId }) || "/switch-view";
    },
    [companyId]
  );

  return (
    <ContractList
      facilityIds={selectedFacilityIds}
      facilities={facilities}
      companyIds={companyId ? [companyId] : []}
      companies={companies}
      onFacilityChange={setSelectedFacilityIds}
      contractors={contractorOptions}
      awardedContractorIds={awardedContractorIds}
      onAwardedContractorChange={setAwardedContractorIds}
      loadRequests={loadRequests}
      getDetailPath={getDetailPath}
      showFacilityColumn
      showRequesterColumn
      canCreate={canCreate}
      viewerRole="facility"
      createPath={buildPortfolioPath("create-contract-request", { companyId }) || "/switch-view"}
    />
  );
}
